import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import Header from '../../components/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Label } from '../../components/ui/label';
import { FileText, ArrowLeft, Save, Calendar, BookOpen, Upload, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const CreateAssignmentPage = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    course_id: '',
    module_id: '',
    lesson_id: '',
    title: '',
    description: '',
    instructions: '',
    due_date: '',
    due_time: '',
    max_score: 100,
    allowed_formats: ['pdf', 'doc', 'docx'],
    allow_late_submission: true
  });

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    try {
      const response = await api.get('/courses');
      setCourses(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des cours');
    }
  };

  const fetchModules = async (courseId) => {
    try {
      const response = await api.get(`/courses/${courseId}`);
      setModules(response.data.modules || []);
      setLessons([]);
      setFormData(prev => ({ ...prev, module_id: '', lesson_id: '' }));
    } catch (error) {
      toast.error('Erreur lors du chargement des modules');
    }
  };

  const handleCourseChange = (value) => {
    setFormData(prev => ({ ...prev, course_id: value }));
    fetchModules(value);
  };

  const handleModuleChange = (value) => {
    setFormData(prev => ({ ...prev, module_id: value, lesson_id: '' }));
    const selectedModule = modules.find(m => m.id.toString() === value);
    setLessons(selectedModule?.lessons || []);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleFormat = (format) => {
    setFormData(prev => ({
      ...prev,
      allowed_formats: prev.allowed_formats.includes(format)
        ? prev.allowed_formats.filter(f => f !== format)
        : [...prev.allowed_formats, format]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.lesson_id || !formData.title) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      // Combine date and time
      let dueDate = null;
      if (formData.due_date) {
        dueDate = formData.due_time 
          ? `${formData.due_date}T${formData.due_time}:00`
          : `${formData.due_date}T23:59:00`;
      }

      await api.post('/homework', {
        lesson_id: parseInt(formData.lesson_id),
        title: formData.title,
        description: `${formData.description}\n\n**Consignes:**\n${formData.instructions}`,
        due_date: dueDate,
        max_score: formData.max_score
      });

      toast.success('Devoir créé avec succès ! Les étudiants ont été notifiés.');
      navigate('/trainer/grading');
    } catch (error) {
      console.error('Error creating assignment:', error);
      toast.error('Erreur lors de la création du devoir');
    } finally {
      setLoading(false);
    }
  };

  const fileFormats = [
    { value: 'pdf', label: 'PDF' },
    { value: 'doc', label: 'Word (.doc)' },
    { value: 'docx', label: 'Word (.docx)' },
    { value: 'txt', label: 'Texte (.txt)' },
    { value: 'zip', label: 'Archive (.zip)' },
    { value: 'jpg', label: 'Image (.jpg)' },
    { value: 'png', label: 'Image (.png)' }
  ];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <Header title="Créer un Devoir" onMenuClick={onMenuClick} />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          className="mb-6"
          onClick={() => navigate('/dashboard')}
          data-testid="back-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au tableau de bord
        </Button>

        <form onSubmit={handleSubmit}>
          {/* Course Selection */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-violet-600" />
                Sélection du Cours
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Cours *</Label>
                  <Select value={formData.course_id} onValueChange={handleCourseChange}>
                    <SelectTrigger data-testid="select-course">
                      <SelectValue placeholder="Sélectionner un cours" />
                    </SelectTrigger>
                    <SelectContent>
                      {courses.map(course => (
                        <SelectItem key={course.id} value={course.id.toString()}>
                          {course.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Module *</Label>
                  <Select 
                    value={formData.module_id} 
                    onValueChange={handleModuleChange}
                    disabled={!formData.course_id}
                  >
                    <SelectTrigger data-testid="select-module">
                      <SelectValue placeholder="Sélectionner un module" />
                    </SelectTrigger>
                    <SelectContent>
                      {modules.map(module => (
                        <SelectItem key={module.id} value={module.id.toString()}>
                          {module.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Leçon *</Label>
                  <Select 
                    value={formData.lesson_id} 
                    onValueChange={(v) => handleInputChange('lesson_id', v)}
                    disabled={!formData.module_id}
                  >
                    <SelectTrigger data-testid="select-lesson">
                      <SelectValue placeholder="Sélectionner une leçon" />
                    </SelectTrigger>
                    <SelectContent>
                      {lessons.map(lesson => (
                        <SelectItem key={lesson.id} value={lesson.id.toString()}>
                          {lesson.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Assignment Details */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-violet-600" />
                Détails du Devoir
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label className="text-sm font-medium mb-2 block">Titre du devoir *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  placeholder="Ex: Projet React - Application Todo"
                  data-testid="input-title"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  placeholder="Décrivez le contexte et les objectifs du devoir..."
                  rows={3}
                  data-testid="input-description"
                />
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Consignes détaillées</Label>
                <Textarea
                  value={formData.instructions}
                  onChange={(e) => handleInputChange('instructions', e.target.value)}
                  placeholder="Instructions spécifiques pour réaliser le devoir..."
                  rows={4}
                  data-testid="input-instructions"
                />
              </div>
            </CardContent>
          </Card>

          {/* Deadline & Grading */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-violet-600" />
                Échéance et Notation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Date limite</Label>
                  <Input
                    type="date"
                    value={formData.due_date}
                    onChange={(e) => handleInputChange('due_date', e.target.value)}
                    data-testid="input-due-date"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Heure limite</Label>
                  <Input
                    type="time"
                    value={formData.due_time}
                    onChange={(e) => handleInputChange('due_time', e.target.value)}
                    data-testid="input-due-time"
                  />
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Note maximale</Label>
                  <Input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.max_score}
                    onChange={(e) => handleInputChange('max_score', parseInt(e.target.value))}
                    data-testid="input-max-score"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-3 mt-4">
                <input
                  type="checkbox"
                  id="allow-late"
                  checked={formData.allow_late_submission}
                  onChange={(e) => handleInputChange('allow_late_submission', e.target.checked)}
                  className="w-4 h-4 text-violet-600 border-slate-300 rounded focus:ring-violet-500"
                />
                <Label htmlFor="allow-late" className="text-sm">
                  Autoriser les soumissions en retard (avec pénalité)
                </Label>
              </div>
            </CardContent>
          </Card>

          {/* File Formats */}
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Upload className="w-5 h-5 text-violet-600" />
                Formats de fichiers autorisés
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {fileFormats.map(format => (
                  <button
                    key={format.value}
                    type="button"
                    onClick={() => toggleFormat(format.value)}
                    className={`px-4 py-2 rounded-lg border transition-all ${
                      formData.allowed_formats.includes(format.value)
                        ? 'bg-violet-100 border-violet-300 text-violet-700 dark:bg-violet-900/30 dark:border-violet-600'
                        : 'bg-slate-50 border-slate-200 text-slate-600 dark:bg-slate-800 dark:border-slate-700'
                    }`}
                  >
                    {formData.allowed_formats.includes(format.value) && (
                      <CheckCircle2 className="w-4 h-4 inline mr-2" />
                    )}
                    {format.label}
                  </button>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Submit */}
          <div className="flex justify-end gap-4">
            <Button 
              type="button" 
              variant="outline"
              onClick={() => navigate('/dashboard')}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={loading}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="submit-assignment"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Création en cours...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <Save className="w-4 h-4" />
                  Créer le devoir
                </span>
              )}
            </Button>
          </div>
        </form>
      </main>
    </div>
  );
};

export default CreateAssignmentPage;
